import "../../../../.cache/static-styles-react/build-javascript/compiled.js";
/** @jsxRuntime automatic @jsxImportSource @terraindigital/static-styles-react */
import { runtimeCss as _runtimeCss, mergeComponentProps as _mergeComponentProps } from "@terraindigital/static-styles-react";
import type { CSSObject, SerializedStyles } from "@terraindigital/static-styles-react";
import React from 'react';
import Button, { ButtonProps, buttonStyles } from '../button';
import { colors } from '../colors';
import Link, { LinkProps } from '../link';
import { breakpoints } from '../tokens';
export type StyledButtonSize = 'default' | 'large';
type StyledButtonStyleOptions = {
  reversed?: boolean;
  size?: StyledButtonSize;
};
type ButtonDimensions = {
  paddingText: number;
  paddingChevron: number;
  height: number;
  fontSize: number;
  bodyGap: number;
  chevronWidth: number;
  chevronOffset: number;
  insetBorder: number;
  glowGap: number;
};
const DEFAULT_DIMS: ButtonDimensions = {
  paddingText: 32,
  paddingChevron: 52,
  height: 44,
  fontSize: 18,
  bodyGap: 40,
  chevronWidth: 46,
  chevronOffset: 14,
  insetBorder: 4,
  glowGap: 4
};
const LARGE_HEIGHT_DT = 132;
const LARGE_HEIGHT_MB = 79;
const scaleDims = (dims: ButtonDimensions, scale: number, height: number): ButtonDimensions => ({
  paddingText: Math.round(dims.paddingText * scale),
  paddingChevron: Math.round(dims.paddingChevron * scale),
  height,
  fontSize: dims.fontSize,
  bodyGap: Math.round(dims.bodyGap * scale),
  chevronWidth: Math.round(dims.chevronWidth * scale),
  chevronOffset: Math.round(dims.chevronOffset * scale),
  insetBorder: dims.insetBorder,
  glowGap: dims.glowGap
});
const getDimensions = (size: StyledButtonSize, isMobile: boolean): ButtonDimensions => {
  if (size === 'default') {
    return isMobile ? {
      ...DEFAULT_DIMS,
      fontSize: 16
    } : DEFAULT_DIMS;
  }
  const height = isMobile ? LARGE_HEIGHT_MB : LARGE_HEIGHT_DT;
  const scale = height / DEFAULT_DIMS.height;
  return scaleDims(DEFAULT_DIMS, scale, height);
};
const chevronPseudo = (insetColor: string, dims: ButtonDimensions, reversed: boolean): Record<string, unknown> => {
  const glow = `${dims.glowGap}px`;
  const chevronSide = {
    transition: '0.2s',
    width: `${dims.chevronWidth}px`,
    content: '""',
    display: 'block',
    position: 'absolute',
    top: '0',
    bottom: '0',
    backgroundColor: colors.primaryOrange,
    zIndex: '-1',
    boxShadow: `0 0 0 ${glow} ${colors.primaryOrange}`
  };
  const bodySide = {
    transition: '0.2s',
    width: `calc(100% - ${dims.bodyGap}px)`,
    content: '""',
    display: 'block',
    position: 'absolute',
    top: '0',
    bottom: '0',
    backgroundColor: colors.primaryOrange,
    zIndex: '-1'
  };
  if (reversed) {
    return {
      '&:before': {
        ...bodySide,
        right: '0',
        left: 'auto',
        borderRadius: '0 500px 500px 0'
      },
      '&:after': {
        ...chevronSide,
        left: `${dims.chevronOffset}px`,
        right: 'auto',
        transform: 'rotate(-135deg)',
        borderLeft: `${dims.insetBorder}px solid ${insetColor}`,
        borderBottom: `${dims.insetBorder}px solid ${insetColor}`
      }
    };
  }
  return {
    '&:before': {
      ...bodySide,
      left: '0',
      borderRadius: '500px 0 0 500px'
    },
    '&:after': {
      ...chevronSide,
      right: `${dims.chevronOffset}px`,
      transform: 'rotate(45deg)',
      borderRight: `${dims.insetBorder}px solid ${insetColor}`,
      borderTop: `${dims.insetBorder}px solid ${insetColor}`
    }
  };
};
const buildSizeStyles = (dims: ButtonDimensions, reversed: boolean, isLarge: boolean): Record<string, unknown> => {
  const paddingLeft = reversed ? dims.paddingChevron : dims.paddingText;
  const paddingRight = reversed ? dims.paddingText : dims.paddingChevron;
  return {
    paddingLeft: `${paddingLeft}px`,
    paddingRight: `${paddingRight}px`,
    height: `${dims.height}px`,
    fontSize: `${dims.fontSize}px`,
    ...(isLarge ? {
      whiteSpace: 'normal',
      flexDirection: 'column',
      gap: '2px'
    } : {})
  };
};
export const styledButtonStyles = (insetColor: string, {
  reversed = false,
  size = 'default'
}: StyledButtonStyleOptions = {}): SerializedStyles => {
  const isLarge = size === 'large';
  const dtDims = getDimensions(size, false);
  const mbDims = getDimensions(size, true);
  const glow = `${dtDims.glowGap}px`;
  return _runtimeCss({
    overflow: 'hidden',
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: '1',
    whiteSpace: 'nowrap',
    ...buildSizeStyles(dtDims, reversed, isLarge),
    '&:hover:before': {
      backgroundColor: colors.primaryOrangeHover
    },
    '&:hover:after': {
      backgroundColor: colors.primaryOrangeHover,
      boxShadow: `0 0 0 ${glow} ${colors.primaryOrangeHover}`
    },
    ...chevronPseudo(insetColor, dtDims, reversed),
    [breakpoints.mb]: {
      ...buildSizeStyles(mbDims, reversed, isLarge),
      ...chevronPseudo(insetColor, mbDims, reversed),
      '&:hover:after': {
        boxShadow: `0 0 0 ${mbDims.glowGap}px ${colors.primaryOrangeHover}`
      },
      '&:disabled': {
        '&:hover:after': {
          boxShadow: `0 0 0 ${mbDims.glowGap}px ${colors.grey400}`
        },
        '&:after': {
          boxShadow: `0 0 0 ${mbDims.glowGap}px ${colors.grey400}`
        }
      }
    },
    fontWeight: 600,
    letterSpacing: '-0.1px',
    color: colors.shadesWhite,
    '&:disabled': {
      '&:hover:before': {
        backgroundColor: colors.grey400
      },
      '&:hover:after': {
        backgroundColor: colors.grey400,
        boxShadow: `0 0 0 ${glow} ${colors.grey400}`
      },
      '&:before': {
        backgroundColor: colors.grey400
      },
      '&:after': {
        backgroundColor: colors.grey400,
        boxShadow: `0 0 0 ${glow} ${colors.grey400}`
      }
    }
  });
};
const StyledButton = ({
  insetColor,
  ...props
}: NoRef<ButtonProps & {
  insetColor?: string;
}>): JSX.Element => <Button {..._mergeComponentProps({
  css: _runtimeCss(styledButtonStyles(insetColor || 'var(--system-white)'))
}, props)} />;
export type StyledButtonAsLinkProps = Omit<LinkProps, 'size'> & {
  insetColor?: string;
  reversed?: boolean;
  size?: StyledButtonSize;
  css?: CSSObject | SerializedStyles | Array<CSSObject | SerializedStyles>;
};
export const StyledButtonAsLink = React.forwardRef<HTMLAnchorElement, StyledButtonAsLinkProps>(({
  insetColor,
  reversed,
  size,
  css: consumerCss,
  ...props
}, ref) => <Link {..._mergeComponentProps({
  ref: ref || null,
  css: [buttonStyles, _runtimeCss(styledButtonStyles(insetColor || 'var(--system-white)', {
    reversed,
    size
  })), consumerCss]
}, props)} />);
StyledButtonAsLink.displayName = 'StyledButtonAsLink';
export default StyledButton;